// La forme stockée d'un node table (`nodeDatas.values.table`) : un seul blob,
// colonnes et lignes ensemble.
//
// Une colonne EST un champ (cf. convex/fields) : mêmes types, mêmes réglages,
// même conversion des valeurs que les custom nodes et les Databases. Elle y
// ajoute sa largeur et son agrégat de pied de colonne, propres à la grille.
//
// Partagé par Convex et le frontend, sans dépendance au runtime.

import type { AggregateId } from "../fields/kind";
import { legacyColor, upgradeFieldDef } from "../fields/legacy";
import { conditionsOf, withoutConditionsOn, type FilterGroup, type SortSpec } from "../fields/query";
import { isPlainObject } from "./isPlainObject";
import { fieldDefSchema, kindOf } from "../fields/registry";
import { FIELD_TYPES, type FieldDef, type FieldType, type RowMeta } from "../fields/types";

export const TABLE_VERSION = 2;

/**
 * Les types qu'une colonne de table peut prendre : ceux du registre, sauf
 * les calculés — une ligne de table n'a ni date de création ni auteur — et
 * la relation, qui lie des lignes de Databases.
 */
export const TABLE_COLUMN_TYPES: FieldType[] = FIELD_TYPES.filter(
  (type) => !kindOf(type).readOnly && type !== "relation",
);

/** Ceux d'une colonne de Database : la relation en plus. */
export const DATABASE_COLUMN_TYPES: FieldType[] = FIELD_TYPES.filter((type) => !kindOf(type).readOnly);

export const ROW_HEIGHTS = ["short", "medium", "tall", "full"] as const;
export type RowHeight = (typeof ROW_HEIGHTS)[number];

export type TableColumn = FieldDef & {
  width?: number;
  // Agrégat affiché en pied de colonne.
  summary?: AggregateId;
};

export type TableRow = {
  id: string;
  cells: Record<string, unknown>;
  /** Ligne de Database : ce que lisent les champs calculés (cf. `readTableSnapshot`). */
  meta?: RowMeta;
};

export type StoredTable = {
  version: typeof TABLE_VERSION;
  columns: TableColumn[];
  rows: TableRow[];
  rowHeight?: RowHeight;
  // La vue est partagée : le node du canvas applique les mêmes filtres et tri
  // que la window.
  filter?: FilterGroup;
  sorts?: SortSpec[];
};

// ── Relecture ─────────────────────────────────────────────────────────────

const MIN_COLUMN_WIDTH = 40;
const MAX_COLUMN_WIDTH = 2000;

/** Largeur et agrégat d'une colonne, gardés s'ils ont un sens pour son type. */
export function withGridProps(field: FieldDef, raw: Record<string, unknown>): TableColumn {
  // Défaut et caractère obligatoire sont des notions de formulaire (template,
  // Database) : une cellule de grille se remplit à la main.
  const { default: _default, required: _required, ...rest } = field;
  const column = rest as TableColumn;
  if (typeof raw.width === "number" && Number.isFinite(raw.width)) {
    column.width = Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, raw.width)));
  }
  if (
    typeof raw.summary === "string" &&
    (kindOf(field.type).aggregates as readonly string[]).includes(raw.summary)
  ) {
    column.summary = raw.summary as AggregateId;
  }
  return column;
}

/**
 * Une colonne écrite (grille, agent, import) : le registre valide sa
 * définition. Refusée avec la raison plutôt que corrigée en silence.
 */
export function parseTableColumn(
  raw: unknown,
  // Une ligne de Database a ses dates (les champs calculés y ont un sens),
  // et peut en lier d'autres (la relation).
  { inDatabase = false }: { inDatabase?: boolean } = {},
): { ok: true; column: TableColumn } | { ok: false; reason: string } {
  if (!isPlainObject(raw)) return { ok: false, reason: "Expected a column object." };
  const { width: _width, summary: _summary, ...definition } = raw;
  const parsed = fieldDefSchema.safeParse(definition);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue?.path.length ? `${issue.path.join(".")}: ` : "";
    return { ok: false, reason: `${path}${issue?.message ?? "Invalid column."}` };
  }
  // Une ligne de table n'a ni date de création ni auteur : pas de colonne
  // calculée (la Database, elle, en aura).
  if (!inDatabase && !TABLE_COLUMN_TYPES.includes(parsed.data.type)) {
    return { ok: false, reason: `"${parsed.data.type}" columns are not available in tables.` };
  }
  return { ok: true, column: withGridProps(parsed.data, raw) };
}

function readColumn(raw: unknown): TableColumn | null {
  if (!isPlainObject(raw)) return null;
  const field = upgradeFieldDef(raw) ?? upgradeFieldDef(salvaged(raw));
  return field ? withGridProps(field, raw) : null;
}

/**
 * Une colonne illisible ferait disparaître ses valeurs avec elle : avant d'y
 * renoncer, on corrige ce qu'une écriture extérieure (app, import) abîme le
 * plus souvent — une couleur d'option hors palette.
 */
function salvaged(raw: Record<string, unknown>): Record<string, unknown> {
  const settings = isPlainObject(raw.settings) ? raw.settings : {};
  if (!Array.isArray(settings.options)) return raw;
  return {
    ...raw,
    settings: {
      ...settings,
      options: settings.options.map((option) =>
        isPlainObject(option) ? { ...option, color: legacyColor(option.color) } : option,
      ),
    },
  };
}

export function readRows(raw: unknown): TableRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (!isPlainObject(row) || typeof row.id !== "string") return [];
    const meta =
      isPlainObject(row.meta) &&
      typeof row.meta.creationTime === "number" &&
      typeof row.meta.updatedAt === "number"
        ? { meta: { creationTime: row.meta.creationTime, updatedAt: row.meta.updatedAt } }
        : {};
    return [{ id: row.id, cells: isPlainObject(row.cells) ? row.cells : {}, ...meta }];
  });
}

/**
 * Une table d'avant le registre des champs, que la migration
 * `upgradeTablesToFields` n'a pas encore réécrite (cf. `tableV1.ts`). Elle se
 * lit vide et refuse les écritures : y écrire repartirait d'une table vide.
 */
export function isLegacyTable(raw: unknown): boolean {
  if (!isPlainObject(raw) || raw.version === TABLE_VERSION) return false;
  return (
    (Array.isArray(raw.columns) && raw.columns.length > 0) ||
    (Array.isArray(raw.rows) && raw.rows.length > 0)
  );
}

/**
 * `values.table` est un `v.any()` : rien ne garantit sa forme. Une table
 * absente, abîmée ou d'avant le registre se lit comme une table vide ; une
 * colonne illisible est ignorée plutôt que de faire échouer toutes les
 * écritures suivantes.
 */
export function readStoredTable(raw: unknown): StoredTable {
  const table = isPlainObject(raw) ? raw : {};
  if (table.version !== TABLE_VERSION) return emptyTable();
  const columns = Array.isArray(table.columns)
    ? table.columns.flatMap((column) => {
        const parsed = readColumn(column);
        return parsed ? [parsed] : [];
      })
    : [];
  return withLiveView({
    version: TABLE_VERSION,
    columns,
    rows: readRows(table.rows),
    ...(isRowHeight(table.rowHeight) ? { rowHeight: table.rowHeight } : {}),
    ...(isPlainObject(table.filter) ? { filter: table.filter as FilterGroup } : {}),
    ...(Array.isArray(table.sorts) ? { sorts: table.sorts as SortSpec[] } : {}),
  });
}

export function isRowHeight(value: unknown): value is RowHeight {
  return typeof value === "string" && (ROW_HEIGHTS as readonly string[]).includes(value);
}

/**
 * Un filtre ou un tri sur une colonne supprimée ne se voit nulle part (plus
 * d'en-tête, donc rien à « clear »), mais compterait toujours : retiré à la
 * lecture et à la suppression.
 */
export function withLiveView(table: StoredTable): StoredTable {
  const ids = new Set(table.columns.map((column) => column.id));
  const next = { ...table };
  if (table.sorts && !table.sorts.every((sort) => ids.has(sort.fieldId))) {
    next.sorts = table.sorts.filter((sort) => ids.has(sort.fieldId));
    if (next.sorts.length === 0) delete next.sorts;
  }
  if (table.filter && !conditionsOf(table.filter).every((c) => ids.has(c.fieldId))) {
    const pruned = withoutConditionsOn(table.filter, (fieldId) => ids.has(fieldId));
    if (pruned) next.filter = pruned;
    else delete next.filter;
  }
  return next;
}

export function emptyTable(): StoredTable {
  return { version: TABLE_VERSION, columns: [], rows: [] };
}

/** Un nom libre parmi ces champs : « Projects », sinon « Projects 2 »… */
export function freeName(fields: readonly FieldDef[], name: string): string {
  const taken = new Set(fields.map((field) => field.name.trim().toLowerCase()));
  if (!taken.has(name.toLowerCase())) return name;
  let n = 2;
  while (taken.has(`${name} ${n}`.toLowerCase())) n += 1;
  return `${name} ${n}`;
}
